import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, ArrowLeft, CheckCircle2, Eye, EyeOff, Star } from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";
import { LanguageSelector } from "@/components/LanguageSelector";

const API = API_URL;

// Decide where to redirect user based on their role and return URL
const getRedirectPath = (user, returnUrl = null) => {
  // If there's a return URL (e.g., /pos), use it
  if (returnUrl && returnUrl !== "/login" && returnUrl !== "/") {
    return returnUrl;
  }
  if (!user) return "/dashboard";
  // Sirviente (atendente) goes directly to POS terminal
  if (user.role === "sirviente") return "/pos-terminal";
  // Everyone else goes to dashboard
  return "/dashboard";
};

function ForgotPasswordForm({ onBack, t }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error(t('login.enterEmail')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/v1/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      toast.success(t('login.checkEmail'));
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('errors.generic');
      toast.error(msg);
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h3 className="text-lg font-bold">{t('login.checkEmail')}</h3>
        <p className="text-sm text-gray-500">{t('login.resetEmailSent')}</p>
        <Button variant="outline" onClick={onBack} className="mt-4">{t('login.backToLogin')}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-gray-700">{t('login.accountEmail')}</Label>
        <Input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder={t('login.emailPlaceholder')}
          className="h-12 bg-gray-50 border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
          data-testid="forgot-email-input"
          required
        />
      </div>
      <Button type="submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="forgot-submit-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('login.sendResetLink')}
      </Button>
      <button type="button" onClick={onBack} className="w-full text-sm text-gray-500 hover:text-emerald-600 flex items-center justify-center gap-1">
        <ArrowLeft className="w-3 h-3" /> {t('login.backToLogin')}
      </button>
    </form>
  );
}

function ResetPasswordForm({ t }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t('login.passwordsNotMatch')); return; }
    if (password.length < 4) { toast.error(t('login.passwordMinLength')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/v1/auth/reset-password`, { token, password });
      setDone(true);
      toast.success(t('login.passwordChanged'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
    setLoading(false);
  };

  if (done) {
    return (
      <div className="space-y-4 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
        <h3 className="text-lg font-bold">{t('login.passwordChanged')}</h3>
        <p className="text-sm text-gray-500">{t('login.canLoginNow')}</p>
        <Button onClick={() => navigate("/login")} className="bg-emerald-600 hover:bg-emerald-700">{t('login.goToLogin')}</Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-gray-700">{t('login.newPassword')}</Label>
        <div className="relative">
          <Input type={showPwd ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder={t('login.newPassword')} className="h-12 bg-gray-50 border-gray-200 pr-10" data-testid="reset-password-input" required />
          <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition" data-testid="toggle-reset-password-btn">
            {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-gray-700">{t('login.confirmPassword')}</Label>
        <div className="relative">
          <Input type={showPwd ? "text" : "password"} value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={t('login.confirmPassword')} className="h-12 bg-gray-50 border-gray-200 pr-10" data-testid="reset-confirm-input" required />
        </div>
      </div>
      <Button type="submit" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="reset-submit-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('login.changePassword')}
      </Button>
    </form>
  );
}

export { ResetPasswordForm };

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // login, register, forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [restaurantName, setRestaurantName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFaUserId, setTwoFaUserId] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState("");
  const [restaurants, setRestaurants] = useState(null); // null = not shown, array = show selector
  const [isLoggingIn, setIsLoggingIn] = useState(false); // Prevent useEffect navigation during login flow
  const { login, login2fa, register, user, authAxios, switchRestaurant } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";

  // Persist ?ref= across page refreshes so the affiliate attribution survives navigation
  useEffect(() => {
    if (refCode && !localStorage.getItem("kaiso_partner_ref")) {
      localStorage.setItem("kaiso_partner_ref", refCode);
    }
  }, [refCode]);
  const effectiveRefCode = refCode || localStorage.getItem("kaiso_partner_ref") || "";

  // Get return URL from query param or location state
  const returnUrl = searchParams.get("returnUrl") || location.state?.from?.pathname || null;

  useEffect(() => {
    if (refCode) navigate(`/signup?ref=${refCode}`, { replace: true });
  }, [refCode, navigate]);

  useEffect(() => {
    if (user && restaurants === null && !isLoggingIn) navigate(getRedirectPath(user, returnUrl));
  }, [user, navigate, restaurants, isLoggingIn, returnUrl]);

  const checkMultiRestaurants = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/auth/my-restaurants`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const list = res.data?.restaurants || [];
      if (list.length > 1) {
        setRestaurants(list);
        return true;
      }
    } catch {}
    return false;
  };

  const handleSelectRestaurant = async (rid) => {
    setLoading(true);
    try {
      await switchRestaurant(rid);
      toast.success(t('login.welcome'));
      navigate(returnUrl || "/dashboard");
    } catch { toast.error(t('errors.generic')); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setIsLoggingIn(true);
    try {
      if (twoFaUserId) {
        await login2fa(twoFaUserId, twoFaCode);
        const hasMulti = await checkMultiRestaurants();
        if (!hasMulti) { toast.success(t('login.welcome')); navigate(getRedirectPath(user, returnUrl)); }
      } else if (mode === "login") {
        const res = await login(email, password);
        if (res.requires_2fa) {
          setTwoFaUserId(res.user?.id);
          toast.info(t('login.enter2faCode'));
          setLoading(false);
          setIsLoggingIn(false);
          return;
        }
        const hasMulti = await checkMultiRestaurants();
        // Get user from response to check role
        const loggedInUser = res.user || {};
        if (!hasMulti) { toast.success(t('login.welcome')); navigate(getRedirectPath(loggedInUser, returnUrl)); }
      } else {
        await register(email, password, name, restaurantName, effectiveRefCode);
        localStorage.removeItem("kaiso_partner_ref");
        toast.success(t('login.accountCreated'));
        navigate(returnUrl || "/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || t('errors.unauthorized');
      toast.error(detail);
    }
    setLoading(false);
    setIsLoggingIn(false);
  };

  const isLogin = mode === "login";

  return (
    <div className="min-h-screen bg-white flex">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gray-900">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/b3af0807-d112-4715-a702-f4ae72e6d92b/images/3e46d53cf1d763dd985101e7ec9861ae7f760f3072964d3d5623d692ea8d75e2.png"
          alt="Kaiso System"
          className="object-cover w-full h-full opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/20 to-gray-900/60" />
        <div className="absolute inset-x-0 top-[15%] flex justify-center">
          <div className="max-w-sm px-8 py-7 text-center bg-gray-900/60 backdrop-blur-md rounded-2xl border border-white/5">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="relative">
                <div className="absolute -inset-2 rounded-2xl bg-emerald-500/30 blur-lg" />
                <img src="https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png" alt="Kaiso System" className="relative w-14 h-14 rounded-xl object-cover ring-2 ring-emerald-500/40" />
              </div>
              <div className="text-left">
                <h1 className="text-3xl font-extrabold text-white uppercase tracking-tight" data-testid="login-brand-name">
                  Kaiso <span className="text-emerald-400">System</span>
                </h1>
                <p className="text-xs text-emerald-300/80 font-medium tracking-widest uppercase mt-0.5">Restaurant Tech Platform</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {t('login.brandDescription')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-8 bg-gray-50">
        {/* Language selector at top right */}
        <div className="absolute top-4 right-4">
          <LanguageSelector variant="minimal" />
        </div>
        
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6 lg:hidden">
            <img src="https://customer-assets.emergentagent.com/job_08f655cb-8b91-4c06-80bd-b94d5f4523ca/artifacts/kxmzqj5h_ChatGPT%20Image%2027%20de%20fev.%20de%202026%2C%2001_06_03.png" alt="Kaiso" className="w-12 h-12 rounded-xl shadow-lg" />
            <div>
              <span className="text-2xl font-extrabold text-gray-800 uppercase tracking-tight" data-testid="login-brand-mobile">Kaiso <span className="text-emerald-600">System</span></span>
              <p className="text-[10px] text-gray-400 font-medium tracking-widest uppercase">Restaurant Tech Platform</p>
            </div>
          </div>

        {/* RESTAURANT SELECTION SCREEN */}
        {restaurants && restaurants.length > 1 ? (
          <Card className="w-full bg-white border-gray-200 shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold uppercase tracking-tight text-gray-900" data-testid="restaurant-select-title">
                {t('login.selectRestaurant')}
              </CardTitle>
              <CardDescription className="text-gray-500">
                {t('login.selectRestaurantDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {restaurants.map((r) => (
                <button
                  key={r.id}
                  onClick={() => handleSelectRestaurant(r.id)}
                  disabled={loading}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-gray-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all text-left group"
                  data-testid={`restaurant-option-${r.slug || r.id.slice(0, 8)}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition">
                    <span className="text-lg font-black text-emerald-700">{(r.name || "R")[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate">{r.name}</p>
                    <p className="text-xs text-gray-400">{r.slug ? `/${r.slug}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.subscription_status === "active" && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{t('login.active')}</span>}
                    {r.subscription_status === "trial" && <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">Trial</span>}
                    <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-emerald-500 transition rotate-180" />
                  </div>
                </button>
              ))}
              <div className="pt-2 border-t">
                <button
                  onClick={() => { setRestaurants(null); navigate("/dashboard"); }}
                  className="w-full text-center text-sm text-gray-500 hover:text-emerald-600 py-2 transition"
                  data-testid="skip-restaurant-select"
                >
                  {t('login.continueWithCurrent')}
                </button>
              </div>
            </CardContent>
          </Card>
        ) : (
        <Card className="w-full bg-white border-gray-200 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-3xl font-bold uppercase tracking-tight text-gray-900">
              {mode === "forgot" ? t('login.recoverPassword') : isLogin ? t('login.signIn') : t('login.createAccount')}
            </CardTitle>
            <CardDescription className="text-gray-500">
              {mode === "forgot" ? t('login.recoverPasswordDesc') : isLogin ? t('login.signInDesc') : t('login.createAccountDesc')}
            </CardDescription>
            {refCode && !isLogin && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 flex items-center gap-2 mt-2" data-testid="referral-badge">
                <Star className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <p className="text-xs text-orange-700">{t('login.referredBy')} <span className="font-bold font-mono">({refCode})</span></p>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {mode === "forgot" ? (
              <ForgotPasswordForm onBack={() => setMode("login")} t={t} />
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700">{t('login.yourName')}</Label>
                        <Input id="name" value={name} onChange={e => setName(e.target.value)} placeholder={t('login.namePlaceholder')} className="h-12 bg-gray-50 border-gray-200 focus:border-emerald-500" required={!isLogin} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="restaurant" className="text-gray-700">{t('login.restaurantName')}</Label>
                        <Input id="restaurant" value={restaurantName} onChange={e => setRestaurantName(e.target.value)} placeholder={t('login.restaurantPlaceholder')} className="h-12 bg-gray-50 border-gray-200 focus:border-emerald-500" required={!isLogin} />
                      </div>
                    </>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">{t('auth.email')}</Label>
                    <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('login.emailPlaceholder')} className="h-12 bg-gray-50 border-gray-200 focus:border-emerald-500" data-testid="login-email-input" required />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label htmlFor="password" className="text-gray-700">{t('auth.password')}</Label>
                      {isLogin && (
                        <button type="button" onClick={() => setMode("forgot")} className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold py-1 px-2 -mr-2 rounded-md hover:bg-emerald-50 transition-colors" data-testid="forgot-password-link">
                          {t('auth.forgotPassword')}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <Input id="password" type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="h-12 bg-gray-50 border-gray-200 focus:border-emerald-500 pr-10" data-testid="login-password-input" required />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition" data-testid="toggle-password-btn">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  {twoFaUserId && (
                    <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="2fa-code-section">
                      <Label className="text-amber-800 font-bold text-sm">{t('login.twoFaCode')}</Label>
                      <p className="text-xs text-amber-600 mb-2">{t('login.twoFaCodeDesc')}</p>
                      <Input
                        value={twoFaCode}
                        onChange={e => setTwoFaCode(e.target.value.replace(/\s/g, ""))}
                        placeholder="000000"
                        className="h-12 bg-white text-center text-xl font-mono tracking-[0.3em] border-amber-300 focus:border-amber-500"
                        maxLength={8}
                        data-testid="2fa-code-input"
                        autoFocus
                      />
                    </div>
                  )}
                  <Button type="submit" className="w-full h-12 font-semibold uppercase tracking-wide bg-emerald-600 hover:bg-emerald-700 text-white" disabled={loading} data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : twoFaUserId ? t('login.verify') : (isLogin ? t('auth.login') : t('auth.createAccount'))}
                  </Button>
                </form>
                <div className="mt-6 text-center">
                  <button onClick={() => setMode(isLogin ? "register" : "login")} className="text-sm text-gray-500 hover:text-emerald-600 transition-colors">
                    {isLogin ? t('login.noAccount') : t('login.hasAccount')}
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}
        </div>
      </div>
    </div>
  );
}
